// A button that asks first, in a modal dialog (Settings: revoking a key, erasing an app user,
// deleting a project, which also asks for its name typed: the dialog opens in that field, its first
// control, and Enter there confirms once the name matches). Instrumentation's "Stop the SDK" asks
// for a reason instead (D114), passed to `onConfirm`.
import { useId, useRef, useState } from 'react'
import { useI18n } from '../i18n'

/** A button that asks first, in a modal dialog. */
export function Confirm({
  label,
  title,
  body,
  confirm,
  onConfirm,
  busy,
  disabled,
  buttonClass = 'btn small danger',
  buttonName,
  confirmClass = 'btn danger solid',
  typed,
  reason,
}: {
  label: string
  title: string
  body: string
  confirm: string
  onConfirm: (reason?: string) => void
  busy?: boolean
  /** Nothing to confirm yet (an empty form). */
  disabled?: boolean
  /** The opening button's class: Console D's pages use `btn ghost sm`. */
  buttonClass?: string
  /**
   * The opening button's accessible name when `label` alone repeats in a list ("Revoke" on every
   * row): it starts with `label` and names the row.
   */
  buttonName?: string
  /**
   * The confirming button's class. Red by default (brand v2, D121: destructive actions confirm in
   * red); pass `btn solid` when confirming is not destructive (cancelling a deletion).
   */
  confirmClass?: string
  /** Confirmed only once `value` is typed exactly, in a field labelled `label`. */
  typed?: { label: string; value: string }
  /** Confirmed only with a reason written, of at most `maxLength` characters, in a field labelled `label`. */
  reason?: { label: string; maxLength: number }
}) {
  const { t } = useI18n()
  const ref = useRef<HTMLDialogElement>(null)
  const titleId = useId()
  const fieldId = useId()
  const [text, setText] = useState('')
  const ready = typed ? text === typed.value : reason ? text.trim().length > 0 : true
  const close = () => {
    ref.current?.close()
    setText('')
  }
  // No <form>: a Confirm may sit inside a page's own form (Erase a user).
  const submit = () => {
    if (!ready) return
    const why = reason ? text.trim() : undefined
    close()
    onConfirm(why)
  }
  return (
    <>
      <button
        type="button"
        className={buttonClass}
        aria-label={buttonName}
        disabled={busy || disabled}
        onClick={() => ref.current?.showModal()}
      >
        {label}
      </button>
      <dialog ref={ref} aria-labelledby={titleId} onClose={() => setText('')}>
        <div className="stack" style={{ gap: 14 }}>
          <h2 id={titleId} style={{ fontSize: 17 }}>
            {title}
          </h2>
          <p className="dim">{body}</p>
          {typed ? (
            <label className="lbl" htmlFor={fieldId}>
              {typed.label}
              <input
                id={fieldId}
                className="field"
                value={text}
                autoComplete="off"
                spellCheck={false}
                translate="no"
                onChange={(e) => setText(e.target.value)}
                onKeyDown={(e) => {
                  if (e.key !== 'Enter') return
                  e.preventDefault()
                  submit()
                }}
              />
            </label>
          ) : reason ? (
            <label className="lbl" htmlFor={fieldId}>
              {reason.label}
              <textarea
                id={fieldId}
                className="field"
                rows={3}
                maxLength={reason.maxLength}
                value={text}
                onChange={(e) => setText(e.target.value)}
              />
            </label>
          ) : null}
          <div className="head-actions">
            <button type="button" className={confirmClass} disabled={!ready} onClick={submit}>
              {confirm}
            </button>
            <button type="button" className="btn" onClick={close}>
              {t('settings.cancel')}
            </button>
          </div>
        </div>
      </dialog>
    </>
  )
}
